import { useEffect, type ReactNode } from 'react';
import './Drawer.css';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Which edge of the viewport the drawer slides in from. */
  side?: 'left' | 'right';
  /** CSS length (e.g. "380px", "min(420px, 94vw)"). Default 380px. */
  width?: string;
  /** "dimmed" greys the stage; "transparent" leaves it visible (good
   *  for tuning where the player must see the strings). */
  backdrop?: 'dimmed' | 'transparent';
  /** Single letter; when provided, the drawer registers a window-level
   *  keydown listener: pressing the letter calls `onToggle`, pressing
   *  Escape calls `onClose`. Inputs/textarea are excluded. */
  shortcut?: string;
  onToggle?: () => void;
  /** Optional footer hint shown at the bottom of the body, e.g.
   *  "T toggle · Esc close". */
  hint?: ReactNode;
  children: ReactNode;
}

/**
 * Slide-in drawer template. Used by Settings, Tuning, Synth — every
 * future side panel should compose against this rather than copy the
 * markup + transitions + shortcut wiring.
 *
 * Visuals are driven by `data-side` (transform direction) and the
 * `--drawer-width` custom property; behavior comes from props.
 */
export function Drawer({
  open,
  onClose,
  title,
  side = 'right',
  width = 'min(380px, 92vw)',
  backdrop = 'dimmed',
  shortcut,
  onToggle,
  hint,
  children,
}: DrawerProps) {
  useEffect(() => {
    if (!shortcut) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === 'Escape') onClose();
      else if (e.key.toLowerCase() === shortcut.toLowerCase()) onToggle?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcut, onToggle, onClose]);

  return (
    <>
      <div
        className={`drawer-backdrop drawer-backdrop--${backdrop}${open ? ' drawer-backdrop--open' : ''}`}
        onClick={onClose}
        aria-hidden={!open}
      />
      <aside
        className={`drawer${open ? ' drawer--open' : ''}`}
        data-side={side}
        style={{ ['--drawer-width' as string]: width }}
        role="dialog"
        aria-label={title}
        aria-hidden={!open}
      >
        <header className="drawer__header">
          <h2 className="drawer__title">{title}</h2>
          <button
            className="drawer__close"
            onClick={(e) => {
              (e.currentTarget as HTMLButtonElement).blur();
              onClose();
            }}
            aria-label={`Close ${title.toLowerCase()}`}
          >
            ×
          </button>
        </header>
        <div className="drawer__body">
          {children}
          {hint && <p className="drawer__hint">{hint}</p>}
        </div>
      </aside>
    </>
  );
}
